const baseProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  viewBox: '0 0 24 24',
  'aria-hidden': 'true',
};

// Numeric `size` is treated as design-pixels and rendered as cqw on the
// 1600 design canvas (1cqw = 16px) so the glyph scales with the canvas.
// Returns null if no size is passed, letting the caller size via className
// (e.g. Tailwind `size-4`, which already scales via the cqw --spacing token).
function sizeStyle(size) {
  if (size == null) return null;
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  return { width: sz, height: sz };
}

// Merge a caller-supplied `size` prop into props.style and strip any
// width/height attributes that would render as fixed CSS pixels. Returns
// the merged props ready to spread on the underlying <svg>.
function withSize(props) {
  const { size, style, width, height, ...rest } = props;
  const sized = sizeStyle(size);
  if (sized) return { ...rest, style: { ...sized, ...style } };
  if (style) return { ...rest, style };
  return rest;
}

function IconSearch(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function IconPrint(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M7 8V4h10v4" />
      <rect x="6" y="14" width="12" height="6" rx="1.5" />
      <path d="M6 10H5a2 2 0 0 0-2 2v4h4" />
      <path d="M18 16h3v-4a2 2 0 0 0-2-2h-1" />
      <path d="M8 12h8" />
    </svg>
  );
}

function IconSettings(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M12 3v4" />
      <path d="M12 17v4" />
      <path d="M4 12h4" />
      <path d="M16 12h4" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
  );
}

function IconSparkles(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m12 3 1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1-4.1-1.4 4.1-1.4z" />
      <path d="m18 14 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M7 7v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  );
}

function IconMagic(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m4 20 8-8" />
      <path d="m13 7 4-4" />
      <path d="m14 3 1 2" />
      <path d="m18 7 2 1" />
      <path d="m11 10 3 3" />
      <path d="m5 14 5 5" />
    </svg>
  );
}

function IconMenu(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function IconSave(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M8 4v6h8V4" />
      <path d="M9 19v-5h6v5" />
    </svg>
  );
}

function IconChartBar(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M5 19V9" />
      <path d="M12 19V5" />
      <path d="M19 19v-7" />
      <path d="M4 19h16" />
    </svg>
  );
}

function IconChevronLeft(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function IconChevronRight(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function IconChevronDown(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function IconPlus(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function IconClose(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </svg>
  );
}

function IconDownload(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M12 4v10" />
      <path d="m8 10 4 4 4-4" />
      <path d="M5 19h14" />
    </svg>
  );
}

function IconCheck(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="m5 12 4.5 4.5L19 7" />
    </svg>
  );
}

// Two interlocked chain-link arcs. Used on the game board to flag
// cards that have equipment attached.
function IconLink(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

// Currency glyphs. Match the style established in the app header so the
// same visual shows up wherever coins or arcana shards are displayed.
// Both icons use low-opacity fill + solid stroke + drop-shadow glow.
export const COIN_ICON_COLOR = '#f0d060';
export const SHARD_ICON_COLOR = '#7dd3fc';
// Warm weathered-stone gold. Sits between Arcana's cyan and Gold's
// bright yellow so the three currencies read as a deliberate trio
// rather than two warm + one cold.
export const KEYSTONE_ICON_COLOR = '#c9a86a';

// Numeric `size` is treated as design pixels and rendered via CSS in cqw
// so the icon scales with the canvas. Pass a string (e.g. "16px") to opt out.
// `size` is a square box for all three currency marks; the SVG centers its
// viewBox inside it, so mixed silhouettes sit level beside each other.
/* Gold. `two-coins`, Delapouite, game-icons.net (CC BY 3.0). A filled
   silhouette holds its read at the inline sizes this mark is used at (10-22);
   the hairline-stroke disc it replaced lost its inner rings below ~14. */
function CoinIcon({ size = 14, glow = true, ...rest }) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  const style = { width: sz, height: sz };
  if (glow) style.filter = 'drop-shadow(0 0 6px rgba(240,208,96,0.4))';
  return (
    <svg viewBox="14 89 484 334" fill={COIN_ICON_COLOR} style={style} aria-hidden="true" {...rest}>
      <path d="M264.4 95.01c-35.6-.06-80.2 11.19-124.2 34.09C96.27 152 61.45 182 41.01 211.3c-20.45 29.2-25.98 56.4-15.92 75.8 10.07 19.3 35.53 30.4 71.22 30.4 35.69.1 80.29-11.2 124.19-34 44-22.9 78.8-53 99.2-82.2 20.5-29.2 25.9-56.4 15.9-75.8-10.1-19.3-35.5-30.49-71.2-30.49zm91.9 70.29c-3.5 15.3-11.1 31-21.8 46.3-22.6 32.3-59.5 63.8-105.7 87.8-46.2 24.1-93.1 36.2-132.5 36.2-18.6 0-35.84-2.8-50.37-8.7l10.59 20.4c10.08 19.4 35.47 30.5 71.18 30.5 35.7 0 80.3-11.2 124.2-34.1 44-22.8 78.8-52.9 99.2-82.2 20.4-29.2 26-56.4 15.9-75.7zm28.8 16.8c11.2 26.7 2.2 59.2-19.2 89.7-18.9 27.1-47.8 53.4-83.6 75.4 11.1 1.2 22.7 1.8 34.5 1.8 49.5 0 94.3-10.6 125.9-27.1 31.7-16.5 49.1-38.1 49.1-59.9 0-21.8-17.4-43.4-49.1-59.9-16.1-8.4-35.7-15.3-57.6-20zm106.7 124.8c-10.2 11.9-24.2 22.4-40.7 31-35 18.2-82.2 29.1-134.3 29.1-21.2 0-41.6-1.8-60.7-5.2-23.2 11.7-46.5 20.4-68.9 26.1 1.2.7 2.4 1.3 3.7 2 31.6 16.5 76.4 27.1 125.9 27.1s94.3-10.6 125.9-27.1c31.7-16.5 49.1-38.1 49.1-59.9z" />
    </svg>
  );
}

/* Arcana Shards. `crystal-shine`, Lorc, game-icons.net (CC BY 3.0). Square
   box, where the old glyph carried a 13:15 one. */
function ShardIcon({ size = 13, glow = true, ...rest }) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  const style = { width: sz, height: sz };
  if (glow) style.filter = 'drop-shadow(0 0 6px rgba(125,211,252,0.4))';
  return (
    <svg viewBox="16 15 484 484" fill={SHARD_ICON_COLOR} style={style} aria-hidden="true" {...rest}>
      <path d="M258.396 21.375l-17.503 64.1c-1.133 2.452-1.782 5.172-1.782 8.05 0 10.634 8.62 19.256 19.255 19.256 10.634 0 19.256-8.62 19.256-19.255 0-.72-.045-1.426-.122-2.125h.022l-.05-.18c-.23-1.917-.737-3.746-1.488-5.45l-17.586-64.395zm118.21 31.494l-46.21 45.77c-6.03 3.254-10.126 9.626-10.126 16.956 0 10.633 8.622 19.254 19.255 19.254.668 0 1.327-.034 1.977-.1 7.608 5.175 14.85 11.125 21.6 17.875 57.872 57.872 57.87 151.418 0 209.29-6.75 6.747-13.99 12.694-21.594 17.868-.65-.066-1.308-.1-1.975-.1-10.634 0-19.256 8.623-19.256 19.256 0 7.006 3.757 13.12 9.352 16.49l46.694 46.252-18.545-70.55c6.468-4.81 12.67-10.137 18.536-16.003l6.608-6.61-.334-.332c3.252-3.637 6.305-7.388 9.183-11.23l71.057 18.68-51.63-52.126c6.54-15.08 10.757-30.926 12.636-46.996l70.61-19.282-70.56-19.267c-1.82-15.925-5.937-31.633-12.343-46.59l51.234-51.727-70.318 18.483c-4.842-6.536-10.22-12.8-16.144-18.723-5.794-5.794-11.916-11.063-18.298-15.824l18.587-70.717zm-236.307.005l17.112 65.107c.218 1.76.662 3.45 1.322 5.032l.153.582c-6.38 4.76-12.498 10.023-18.29 15.814-5.92 5.92-11.294 12.18-16.136 18.715L54.024 139.61l51.31 51.802c-6.41 14.945-10.535 30.64-12.376 46.553l-70.562 19.27L92.95 276.5c1.856 16.108 6.06 31.994 12.595 47.105l-51.574 52.07 70.952-18.648c4.722 6.312 9.94 12.368 15.676 18.102 5.815 5.814 11.96 11.102 18.367 15.876-1.004 2.215-1.613 4.646-1.707 7.213l-16.678 63.456 48.91-48.447-.037-.08c3.86-3.52 6.297-8.575 6.297-14.21 0-10.634-8.622-19.256-19.256-19.256-.395 0-.786.015-1.176.04-7.573-5.16-14.783-11.088-21.506-17.81-57.872-57.872-57.872-151.417 0-209.29 6.725-6.723 13.938-12.65 21.514-17.81.39.022.782.036 1.178.036 10.634 0 19.254-8.62 19.254-19.254 0-6.22-2.963-11.736-7.54-15.256L140.3 52.875zm127.436 89.87v49.02l33.602 19.292 46.18-25.045-79.782-43.268zm-18.69.312l-79.722 42.957 46.31 24.955 33.413-19.062v-48.85zM159.9 202.164v114.012l46.346-24.975v-64.063L159.9 202.164zm196.985.027l-46.342 25.134v63.7l46.342 25.136V202.19zm-98.367 5.83l-33.584 19.158v64.285l33.584 19.162 33.336-19.145v-64.318l-33.336-19.14zm43.082 99.416l-33.864 19.445v47.056l78.24-42.432-44.376-24.068zm-86.24.084l-44.495 23.976 78.182 42.127v-46.885L215.36 307.52zm43.005 94.234c-10.634 0-19.254 8.622-19.254 19.256 0 2.74.582 5.342 1.615 7.703l17.67 64.713 17.787-65.12c.57-1.39.965-2.86 1.197-4.388l.06-.23h-.026c.122-.878.207-1.767.207-2.678 0-10.634-8.62-19.256-19.255-19.256z" />
    </svg>
  );
}

/* Watching eye. `semi-closed-eye`, Lorc, game-icons.net (CC BY 3.0). The
   hub's looking-for-a-match plate while a search runs. */
function IconEyeOpen({ size = 14, color = 'currentColor', ...rest }) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  return (
    <svg viewBox="0 0 512 512" fill={color} style={{ width: sz, height: sz }} aria-hidden="true" {...rest}>
      <path d={EYE_OPEN_PATH} />
    </svg>
  );
}

/* Closed eye. The same `semi-closed-eye` glyph with only its lower lid
   arcs showing, recentred: the lids drawn shut in the identical stroke, so
   the two states read as one eye rather than two icons. */
const EYE_OPEN_PATH = "M258.063 19.5c-117.45 0-215.188 85.162-234.844 197.03l-.44.345.314.406a238.704 238.704 0 0 0-3.032 25.814c4.73-4 9.484-7.87 14.25-11.625l.063.06c146.48-115.9 305.707-112.07 447.344 0l.03-.03c4.787 3.76 9.572 7.615 14.313 11.625a238.268 238.268 0 0 0-3.032-25.875l.283-.375c-.135-.106-.272-.206-.407-.313C473.26 104.68 375.522 19.5 258.062 19.5zm0 18.688c101.242 0 186.342 68.277 211.906 161.343-66.862-46.73-138.216-70.87-210.064-71.31-72.09-.444-144.657 22.953-213.72 71.25 25.59-93.032 110.66-161.282 211.876-161.282zm1.843 126.906c-19.728-.138-39.508 1.77-59.28 5.75-28.27 18.638-46.876 50.68-46.876 87.156 0 36.69 18.828 68.857 47.375 87.438 19.428 3.744 38.904 5.57 58.344 5.468 18.58-.098 37.148-1.952 55.624-5.53 28.473-18.593 47.25-50.737 47.25-87.376 0-36.436-18.573-68.447-46.78-87.094-18.504-3.747-37.068-5.682-55.658-5.812zM162 181.314c-44.358 15.08-88.47 40.774-131.625 77.624 42.734 36.39 87.183 61.755 132.22 76.5-17.2-21.155-27.532-48.113-27.532-77.438 0-28.983 10.098-55.64 26.937-76.688zm192.188.092C370.976 202.44 381.03 229.06 381.03 258c0 29.296-10.305 56.23-27.467 77.375 45.355-14.86 89.806-40.357 132.156-76.406-42.67-36.427-86.778-62.318-131.533-77.564zm-96.125 17.375c6.243 0 12.255.957 17.906 2.75-9.1 4.638-15.345 14.09-15.345 25 0 15.492 12.572 28.064 28.063 28.064 11.16 0 20.795-6.534 25.312-15.97a59.072 59.072 0 0 1 3.25 19.376c0 32.69-26.498 59.188-59.188 59.188S198.875 290.69 198.875 258s26.498-59.22 59.188-59.22zm237.875 76.095a488.107 488.107 0 0 1-20.594 16.406c-.022.147-.04.293-.063.44-68.965 55.238-142.036 83.367-215.467 83.842-73.467.476-147.678-26.688-218.97-83.78-.022-.146-.04-.292-.062-.438a490.628 490.628 0 0 1-20.593-16.438C28.874 398.63 132.153 496.47 258.063 496.47c125.92 0 229.204-97.856 237.875-221.595zm-448.782 45.22c68.69 50.006 140.915 74.62 212.78 74.155 71.622-.463 142.556-25.776 209.033-74.156-26.753 91.21-110.98 157.687-210.908 157.687-99.92 0-184.145-66.49-210.906-157.686z";
function IconEyeClosed({ size = 14, color = 'currentColor', ...rest }) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  return (
    <svg viewBox="0 0 512 512" fill={color} style={{ width: sz, height: sz }} aria-hidden="true" {...rest}>
      {/* The open glyph's outer ring, redrawn as a stroke of the same weight. */}
      <circle cx="256" cy="258" r="229" fill="none" stroke={color} strokeWidth="19" />
      <circle cx="256" cy="258" r="203" fill="none" stroke={color} strokeWidth="7" />
      {/* Its lower lid arcs, scaled into the ring and lifted to the centre. */}
      <g transform="translate(256 258) scale(0.84) translate(-256 -258) translate(0 -168)">
        <path d={EYE_OPEN_PATH} clipPath="url(#vh-eye-closed-clip)" />
      </g>
      <defs>
        <clipPath id="vh-eye-closed-clip"><rect x="0" y="380" width="512" height="132" /></clipPath>
      </defs>
    </svg>
  );
}

/* Keystones. `rune-stone`, Lorc, game-icons.net (CC BY 3.0). Square box,
   where the hand-drawn runestone carried a 14:16 one. */
function KeystoneIcon({ size = 14, glow = true, ...rest }) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  const style = { width: sz, height: sz };
  if (glow) style.filter = 'drop-shadow(0 0 6px rgba(201,168,106,0.45))';
  return (
    <svg viewBox="31 14 468 483" fill={KEYSTONE_ICON_COLOR} style={style} aria-hidden="true" {...rest}>
      <path d="M153.568 20.424L109.13 70.79l63.696 291.11-18.746 1.758L92.266 81.143 36.588 137.73 110.28 427.7l41.2-47.26 13.286 13.2-43.715 50.147L322.1 491.56l53.714-49.52-192.472-50.067 5.084-17.99 199.982 52.023 103.87-195.3-68.82-127.98-269.888-82.302zm81.912 51.892c80.62.774 168.87 79.368 155.422 164.98-28.148-67.998-92.725-132.962-165.6-115.685-63.013 14.94-5.186 80.4 65.82 97.693C369.54 238.4 459.874 302.575 400.1 365.79c-55.167 58.34-159.148 2.03-150.717-67.73 17.855 32.638 81.77 85.592 120.695 43.436 54.56-59.084-77.733-93.542-122.27-112.883-45.846-19.903-138.904-86.006-81.083-133.1 20.11-16.38 44.074-23.433 68.753-23.197zm21.893 61.647c7.13.043 15.174.844 23.918 2.453-25.35 11.378-30.295 31.156-25.52 54.936-48.725-35.784-36.897-57.624 1.603-57.39zm140.693 21.39c38.708 17.26 53.486 66.86 8.854 96.036 6.68-32.38 6.314-64.494-8.854-96.038zM246.77 246.485c5.027.003 10.616 1.487 15.886 4.53 12.047 6.955 17.874 19.418 13.014 27.836-4.86 8.417-18.567 9.603-30.613 2.648-12.047-6.955-17.872-19.418-13.012-27.836 2.734-4.735 8.265-7.18 14.726-7.178z" />
    </svg>
  );
}

/* ── Settings-tab icons ─────────────────────────────────
 * Medieval-themed glyphs for the horizontal settings tab bar. Drawn
 * in the same stroke-based style as the rest of the icon set so they
 * blend with the game's visual language instead of looking like
 * third-party library drop-ins. All icons are 24×24 with
 * strokeWidth 1.8 and inherit currentColor, so the tab's gold/bronze
 * active state tints them automatically.
 */

// Display. Scrying orb on a stand. A circle with a reflected-light
// highlight arc, mounted on a small pedestal. Reads as "the window
// through which you see the world."
function IconScryingOrb(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <circle cx="12" cy="10" r="6.5" />
      <path d="M8.5 7.5c1-1 2.5-1.6 4-1.6" />
      <path d="M9 21h6" />
      <path d="M12 16.5v4.5" />
    </svg>
  );
}

// Accessibility. Classic open eye. Universal "sight" glyph, equally
// readable as accessibility in a modern UI and as a heraldic "watching
// eye" in medieval iconography.
function IconEye(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// Controls. Keyboard. A row-of-keys silhouette is the universal
// "input / controls" glyph for keyboard-and-mouse games. Rounded
// rectangle body, two rows of small key marks, and a longer spacebar
// at the bottom. Reads instantly at 18px.
function IconKeyboard(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <path d="M5.5 10h1M8.5 10h1M11.5 10h1M14.5 10h1M17.5 10h1" />
      <path d="M7 13h1M10 13h1M13 13h1M16 13h1" />
      <path d="M7.5 16h9" />
    </svg>
  );
}

// Sound. Hanging bell. Distinctive T-shaped hanger on top, rounded
// dome body, and a flared rim that's wider than the dome (the
// universally-read "bell mouth") with a clapper hanging below. The
// wider rim + hanging clapper is what separates this from a lamp
// silhouette.
function IconBell(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M12 4v2" />
      <path d="M11 4h2" />
      <path d="M7 16V11a5 5 0 0 1 10 0v5" />
      <path d="M5 16h14" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

// Discord. Discord's official Clyde mascot silhouette. Rounded body
// with two downward-pointing "feet" at the bottom (the mouth shape)
// and two oval eyes inside. Immediately brand-recognizable.
function IconDiscord(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M7 4 Q5 4 5 6 V15 Q5 17 7 17 L7 20 L10 17 H14 L17 20 L17 17 Q19 17 19 15 V6 Q19 4 17 4 Z" />
      <ellipse cx="9.5" cy="11" rx="1.3" ry="1.8" fill="currentColor" stroke="none" />
      <ellipse cx="14.5" cy="11" rx="1.3" ry="1.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Account. Royal crown. Three-peak silhouette with jewels at each
// point. Immediately reads as "your profile / royal identity."
function IconCrown(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M3 18 5 9l4 4 3-7 3 7 4-4 2 9z" />
      <path d="M4 21h16" />
      <circle cx="5" cy="9" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="19" cy="9" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="6" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Skill tree. A 3×3 lattice of nodes connected by a central trunk
// and diagonal branches at the top tier. Reads as "skill / mastery
// tree" at small sizes. Used in the AppHeader as the entry button to
// the skill tree screen, and mirrored by the "View Skill Tree" button
// on the profile overlay so both surfaces use the same glyph.
function IconSkillTree(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <circle cx="12" cy="4" r="1.5" />
      <circle cx="5" cy="11" r="1.5" />
      <circle cx="12" cy="11" r="1.5" />
      <circle cx="19" cy="11" r="1.5" />
      <circle cx="5" cy="20" r="1.5" />
      <circle cx="12" cy="20" r="1.5" />
      <circle cx="19" cy="20" r="1.5" />
      <path d="M12 5.5v4M5 12.5v6M12 12.5v6M19 12.5v6M6.1 10.1l4.4-4.4M17.9 10.1l-4.4-4.4" />
    </svg>
  );
}

// ── Nordic-rune skill-path icons ──────────────────────
// Hand-drawn Elder Futhark runes, one per skill path. Drawn with a
// thicker stroke (2.4 vs the 1.8 baseline) so they read as stone-
// carved marks rather than thin glyphs. Each rune happens to match
// its path's theme. Bonus thematic resonance, not a requirement.
//
// Fehu (ᚠ). "wealth / cattle." Merchant
function IconRuneFehu(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...withSize(props)}
    >
      <path d="M8 3 V 21" />
      <path d="M8 5 L 17 3" />
      <path d="M8 11 L 17 9" />
    </svg>
  );
}

// Kenaz (ᚲ). "torch / knowledge / creation." Spellcraft
function IconRuneKenaz(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...withSize(props)}
    >
      <path d="M16 3 L 8 12 L 16 21" />
    </svg>
  );
}

// Ansuz (ᚨ). "wisdom / divine voice." Scholar
function IconRuneAnsuz(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...withSize(props)}
    >
      <path d="M8 3 V 21" />
      <path d="M8 6 L 16 10" />
      <path d="M8 10 L 16 14" />
    </svg>
  );
}

// Algiz (ᛉ). "elk / protection / instinct." Hunter
function IconRuneAlgiz(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...withSize(props)}
    >
      <path d="M12 3 V 21" />
      <path d="M12 8 L 6 3" />
      <path d="M12 8 L 18 3" />
    </svg>
  );
}

// Dagaz (ᛞ). "dawn / breakthrough / transformation." Transmutation
function IconRuneDagaz(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...withSize(props)}
    >
      <path d="M5 3 V 21" />
      <path d="M19 3 V 21" />
      <path d="M5 3 L 19 21" />
      <path d="M19 3 L 5 21" />
    </svg>
  );
}

// Path icons. Medieval-game silhouettes for the five skill paths.
// Drawn in the same stroke vocabulary as the rest of the icon set
// (strokeWidth 1.8, currentColor) so they accept the path's accent
// color via inline `style.color`. Inspired by the recognizable
// open-source game-icon iconography (game-icons.net style).
// drawstring sacks, anvils, spellbooks, longbows, banded chests.

// Merchant. Drawstring coin sack with embossed coin face. The
// classic "merchant's purse" silhouette: pinched neck, bulbous
// body, a coin showing through the front.
function IconCoinSack(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Drawstring loop */}
      <path d="M9.5 5 C 10 3.5 14 3.5 14.5 5" />
      {/* Tied neck of the sack */}
      <path d="M9.5 5 L 8 7 H 16 L 14.5 5" />
      {/* Pear-shaped sack body */}
      <path d="M8 7 Q 4 10 4 15 Q 4 21 12 21 Q 20 21 20 15 Q 20 10 16 7" />
      {/* Embossed coin */}
      <circle cx="12" cy="14" r="2.4" />
      {/* Coin cross detail */}
      <path d="M12 12.5 V 15.5 M 10.5 14 H 13.5" strokeWidth="1.2" />
    </svg>
  );
}

// Artisan. Anvil with horn beak. Wide flat top with the
// distinctive horn protruding to one side, narrow waist, flared
// base. The single most readable "blacksmith / forge" silhouette.
function IconAnvil(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M3 9 H17 L21 11 L17 13 V16 H19 V18 H5 V16 H7 V13 H3 Z" />
      {/* Hammer dent on top */}
      <path d="M9 11 H 13" strokeWidth="1.2" />
    </svg>
  );
}

// Scholar. Open spellbook with a star/rune floating above. The
// book is drawn as two angled pages meeting at a central spine
// (more dimensional than a flat rectangle); the rune signals
// "magical / knowledge" rather than just "book."
function IconSpellbook(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Rune star floating above */}
      <path d="M12 3.5 L 12.7 5 L 14.2 5.2 L 13.1 6.3 L 13.4 7.8 L 12 7 L 10.6 7.8 L 10.9 6.3 L 9.8 5.2 L 11.3 5 Z" strokeWidth="1.2" />
      {/* Left page */}
      <path d="M12 10 L 4 8 V 19 L 12 21 Z" />
      {/* Right page */}
      <path d="M12 10 L 20 8 V 19 L 12 21 Z" />
      {/* Spine */}
      <path d="M12 10 V 21" />
    </svg>
  );
}

// Tome. Closed leather-bound book viewed front-on, with a thick
// spine showing two raised binding bands (a hallmark of medieval
// codices) and a single metal clasp wrapping across the foredge.
// No runes or stars; the silhouette alone signals "old book."
function IconTome(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Cover */}
      <rect x="5" y="3" width="14" height="18" rx="0.6" />
      {/* Inner spine division */}
      <path d="M8.5 3 V 21" />
      {/* Raised binding bands on the spine */}
      <path d="M5 8 H 8.5" />
      <path d="M5 16 H 8.5" />
      {/* Clasp strap wrapping across to the foredge */}
      <path d="M13 10.5 H 19" />
      <path d="M13 13.5 H 19" />
      <path d="M13 10.5 V 13.5" />
    </svg>
  );
}

// Atlas. Three-panel folded map with a small terrain peak in the
// centre. Reads as "geography / sites deck" alongside IconSpellbook.
function IconAtlas(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Folded map silhouette (outer panels tilt down at the folds) */}
      <path d="M3 7 L 9 5 L 15 7 L 21 5 V 18 L 15 20 L 9 18 L 3 20 Z" />
      {/* Vertical fold seams */}
      <path d="M9 5 V 18" />
      <path d="M15 7 V 20" />
      {/* Terrain peak on the centre panel */}
      <path d="M10.5 14 L 12 11.5 L 13.5 14" strokeWidth="1.2" />
    </svg>
  );
}

// Hunter. Drawn longbow with arrow nocked and aimed right. The
// bow's curve + bent string + arrow shaft + head + fletching all
// read instantly as "archer / hunt."
/* Crossed swords. Lorc, game-icons.net (CC BY 3.0). Valor: what a match is
   won with. Same icon the Armarium uses for its table tab. */
function IconSwords(props) {
  const { size, style, width, height, ...rest } = props;
  return (
    <svg viewBox="14 8 484 481" fill="currentColor" aria-hidden="true" {...withSize({ size, style, width, height })} {...rest}>
      <path d="M19.75 14.438c59.538 112.29 142.51 202.35 232.28 292.718l3.626 3.75.063-.062c21.827 21.93 44.04 43.923 66.405 66.25-18.856 14.813-38.974 28.2-59.938 40.312l28.532 28.53 68.717-68.717c42.337 27.636 76.286 63.646 104.094 105.81l28.064-28.06c-42.47-27.493-79.74-60.206-106.03-103.876l68.936-68.938-28.53-28.53c-11.115 21.853-24.413 42.015-39.47 60.593-43.852-43.8-86.462-85.842-130.125-125.47-.224-.203-.432-.422-.656-.625C183.624 122.75 108.515 63.91 19.75 14.437zm471.875 0c-83.038 46.28-154.122 100.78-221.97 161.156l22.814 21.562 56.81-56.812 13.22 13.187-56.438 56.44 24.594 23.186c61.802-66.92 117.6-136.92 160.97-218.72zm-329.53 125.906l200.56 200.53c-4.36 4.443-8.84 8.793-13.405 13.032L148.875 153.53l13.22-13.186zm-76.69 113.28l-28.5 28.532 68.907 68.906c-26.29 43.673-63.53 76.414-106 103.907l28.063 28.06c27.807-42.164 61.758-78.174 104.094-105.81l68.718 68.717 28.53-28.53c-20.962-12.113-41.08-25.5-59.937-40.313 17.865-17.83 35.61-35.433 53.157-52.97l-24.843-25.655-55.47 55.467c-4.565-4.238-9.014-8.62-13.374-13.062l55.844-55.844-24.53-25.374c-18.28 17.856-36.602 36.06-55.158 54.594-15.068-18.587-28.38-38.758-39.5-60.625z" />
    </svg>
  );
}

/* Laurels. Lorc, game-icons.net (CC BY 3.0). Renown earned over a career,
   which is what account experience is. */
function IconLaurels(props) {
  const { size, style, width, height, ...rest } = props;
  return (
    <svg viewBox="15 12 481 488" fill="currentColor" aria-hidden="true" {...withSize({ size, style, width, height })} {...rest}>
      <path d="M234.7 18.05c-21 .2-38.8 2.5-62 10.2-4.1 2-8.2 4.1-12.2 6.2.8 5.26 3.2 10.77 5.5 14.7-4.9 4.2-9.6 8.4-14.1 12.8-3.7-5.5-6.6-11.4-8.3-17.4-14.2 9.2-27.7 19.6-40.1 31.4 1.9 9.5 9.2 18.21 15.2 24.15-3.7 5.2-7.2 10.4-10.5 15.7-8.22-7.2-15.12-15.5-19.32-24.65C74.97 108.1 61.92 126 53.08 142.3c5.29 13 19.01 22.7 29.8 28.4-2 6.1-3.7 12.2-5.1 18.4-13.5-6.4-26.3-15.7-34.5-26.6-8.7 20.1-14.7 40.7-18.2 61.4 9.63 15.5 30.57 22.9 46 25.9.1 6.4.4 12.8.9 19.2-17.79-2.7-37.26-9.6-49.9-20.4-1.6 22.3-.5 44.5 3.4 66.2 15.25 13.7 41.14 15.3 58.6 13.7 2 6.1 4.1 12.2 6.5 18.1-18.61 4.5-43.29 1.1-59.3-6.2 6.6 23.7 16.4 46.4 29.2 67.4 19.33 8.6 44.52 3.6 61.72-2.5 3.7 5.3 7.6 10.5 11.6 15.5-17.8 9.5-39.9 11.5-57.52 10.1 12.3 16.3 26.62 31.2 42.72 44.4 4.9 1.1 10.5 1.1 16.7.3 11.7-1.7 25.2-7 37.9-14.7 16.7 13.5 34.9 24.7 54.1 33.1l7.5-17.2c-16-6.9-31.3-16.2-45.6-27.3 13.3-10.9 24.3-24 30.2-36.5 4.7-9.7 6.3-18.4 4.5-26.3-10.7-5.7-20.6-12.5-29.5-20.3-7.8 20.8-26.4 36.1-43.5 46-4-4.9-7.9-9.9-11.6-15 16.8-9.8 39.9-27.5 39.1-47.1-8.9-10.3-16.6-21.8-22.9-34.1-12 14-30.7 22.5-46.5 26.7-2.4-5.8-4.6-11.6-6.6-17.6 16.8-5.2 37.9-13 44.1-29.7-4.3-11.5-7.5-23.6-9.7-36-13.8 8.4-32 11.1-46.32 10.9-.6-6.2-1-12.4-1.2-18.7 15.52-.6 33.92-2.5 44.92-14.3-.8-12.6-.5-25.5.9-38.5-13.4 2.8-29 .3-40.42-3.2 1.3-6 2.9-12.1 4.8-18.1 12.82 3.2 27.12 6.7 38.82.8 2.7-13.6 6.7-27.3 12-40.8-9.9-1.8-20.2-6.3-27.7-10.7 3.3-5.3 6.8-10.5 10.5-15.7 8.1 4.2 16.3 8.8 25.2 8.4 5.7-11.6 12.3-22.65 19.5-32.75-5.1-2.7-10-6.4-14.4-10.6 4.4-4.3 9.1-8.5 13.9-12.7 3.8 3.54 8 6.18 12.3 8.2 15.9-18.6 35.9-36.23 49-53.8zm38.4 0c15.4 20.75 33.8 35.63 48.9 53.7 4.6-1.76 9.1-5.23 12.3-8.1 4.9 4.2 9.5 8.4 13.9 12.7-4.4 4.2-9.2 7.9-14.4 10.6 7.3 10.1 13.9 21.05 19.6 32.65 9-.1 18.4-4.4 25.2-8.4 3.7 5.2 7.2 10.4 10.4 15.7-8.8 5.9-18.2 9.6-27.6 10.7 5.3 13.5 9.3 27.2 12 40.8 12.3 5.4 27.3 2.7 38.7-.8 1.9 6 3.5 12.1 4.9 18.1-14.2 3.4-27.3 6.2-40.4 3.3 1.4 12.9 1.6 25.8.8 38.5 11.4 12.3 30.2 14.4 44.9 14.2-.2 6.3-.5 12.5-1.2 18.7-17.1-.5-32.8-2.5-46.3-10.9-2.1 12.4-5.3 24.5-9.6 36.1 8.2 17.4 27.8 25.3 44.1 29.6-2 6-4.2 11.8-6.6 17.6-18.5-5.6-34.9-13-46.6-26.7-6.3 12.4-13.9 23.8-22.9 34.1 1.5 22.4 22.4 37.8 39.2 47.1-3.7 5.1-7.6 10.1-11.6 15-19-11.8-36.6-25.8-43.5-46-9 7.8-18.8 14.6-29.6 20.3-1.8 7.9-.1 16.6 4.5 26.3 6 12.5 17 25.6 30.3 36.5-14.3 11.1-29.6 20.4-45.6 27.3l7.4 17.2c19.3-8.4 37.4-19.6 54.1-33.2 12.7 7.8 26.2 13.1 38 14.8 6.2.8 11.8.8 16.7-.3 16.1-13.2 30.4-28.1 42.7-44.4-18 1.7-37.9-2.3-56.5-9.7-.3-.1-.7-.3-1.1-.4 4.1-5 7.9-10.2 11.7-15.5 18.2 7.8 43.7 11.7 61.6 2.5 12.8-21 22.6-43.7 29.2-67.4-.4.2-.8.4-1.2.5-20.5 6.4-40.1 7.6-58.1 5.7 2.4-5.9 4.5-12 6.5-18 19.1 1.7 45.2.1 58.6-13.8 3.9-21.7 5.1-43.9 3.4-66.2-14.4 10.7-34.9 17.9-49.9 20.4.5-6.4.9-12.8 1-19.2 16.8-4.8 37.9-10 45.9-25.9-3.5-20.7-9.5-41.3-18.2-61.4-9.4 11.6-23.1 21-34.4 26.5-1.5-6.1-3.2-12.2-5.2-18.3 12-7.4 25.1-15.3 29.9-28.4-10.1-18.7-22.2-35.8-35.9-51.05-4.2 9.05-11.1 17.45-19.2 24.65-3.3-5.3-6.8-10.5-10.6-15.7 6.2-7.17 14.2-14.71 15.2-24.15-12.4-11.8-25.8-22.2-40-31.4-1.8 6-4.7 11.9-8.3 17.4-4.5-4.4-9.2-8.6-14.1-12.8 2.7-4.82 4.7-9.62 5.4-14.7-4-2.1-8.1-4.2-12.2-6.2-24.7-8.2-43.3-10.3-66.2-10.2z" />
    </svg>
  );
}

/* Scroll, unfurled. Lorc, game-icons.net (CC BY 3.0). The season pass is a
   list of trials worked down over the season, so it carries the scroll. */
function IconSeasonScroll(props) {
  const { size, style, width, height, ...rest } = props;
  return (
    <svg viewBox="25 12 471 486" fill="currentColor" aria-hidden="true" {...withSize({ size, style, width, height })} {...rest}>
      <path d="M103.432 17.844c-1.118.005-2.234.032-3.348.08-2.547.11-5.083.334-7.604.678-20.167 2.747-39.158 13.667-52.324 33.67-24.613 37.4 2.194 98.025 56.625 98.025.536 0 1.058-.012 1.583-.022v.704h60.565c-10.758 31.994-30.298 66.596-52.448 101.43-2.162 3.4-4.254 6.878-6.29 10.406l34.878 35.733-56.263 9.423c-32.728 85.966-27.42 182.074 48.277 182.074v-.002l9.31.066c23.83-.57 46.732-4.298 61.325-12.887 4.174-2.458 7.63-5.237 10.467-8.42h-32.446c-20.33 5.95-40.8-6.94-47.396-25.922-8.956-25.77 7.52-52.36 31.867-60.452 5.803-1.93 11.723-2.834 17.565-2.834v-.406h178.33c-.57-44.403 16.35-90.125 49.184-126 23.955-26.176 42.03-60.624 51.3-94.846l-41.225-24.932 38.272-6.906-43.37-25.807h-.005l.002-.002.002.002 52.127-8.85c-5.232-39.134-28.84-68.113-77.37-68.113C341.14 32.26 222.11 35.29 149.34 28.496c-14.888-6.763-30.547-10.723-45.908-10.652zm.464 18.703c13.137.043 27.407 3.804 41.247 10.63l.033-.07c4.667 4.735 8.542 9.737 11.68 14.985H82.92l10.574 14.78c10.608 14.83 19.803 31.99 21.09 42.024.643 5.017-.11 7.167-1.814 8.836-1.705 1.67-6.228 3.875-15.99 3.875-40.587 0-56.878-44.952-41.012-69.06C66.238 46.64 79.582 39.22 95.002 37.12c2.89-.395 5.863-.583 8.894-.573zM118.5 80.78h46.28c4.275 15.734 3.656 33.07-.544 51.51H131.52c1.9-5.027 2.268-10.574 1.6-15.77-1.527-11.913-7.405-24.065-14.62-35.74zm101.553 317.095c6.44 6.84 11.192 15.31 13.37 24.914 3.797 16.736 3.092 31.208-1.767 43.204-4.526 11.175-12.576 19.79-22.29 26h237.19c14.448 0 24.887-5.678 32.2-14.318 7.312-8.64 11.2-20.514 10.705-32.352-.186-4.473-.978-8.913-2.407-13.18l-69.91-8.205 42.017-20.528c-8.32-3.442-18.64-5.537-31.375-5.537H220.053zm-42.668.506c-1.152-.003-2.306.048-3.457.153-2.633.242-5.256.775-7.824 1.63-15.11 5.02-25.338 21.54-20.11 36.583 3.673 10.57 15.347 17.71 25.654 13.938l1.555-.57h43.354c.946-6.36.754-13.882-1.358-23.192-3.71-16.358-20.543-28.483-37.815-28.54z" />
    </svg>
  );
}

function IconLongbow(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Bow curve */}
      <path d="M5 3 Q 2 12 5 21" />
      {/* Bowstring drawn back to point */}
      <path d="M5 3 L 10 11 L 5 21" />
      {/* Arrow shaft */}
      <path d="M10 11 H 21" />
      {/* Arrowhead */}
      <path d="M19 9 L 21 11 L 19 13" />
      {/* Fletching */}
      <path d="M10 9 L 12 11 L 10 13" />
    </svg>
  );
}

// Transmuter. Alchemical flask with cork stopper, narrow neck,
// rounded body, and floating bubbles inside. Universal symbol of
// alchemy / transmutation; reads instantly as "the path that
// transforms what's in your packs into something else."
function IconAlchemyFlask(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Cork stopper */}
      <rect x="9.5" y="2.5" width="5" height="2.6" rx="0.4" />
      {/* Neck rails */}
      <path d="M11 5 V 9" />
      <path d="M13 5 V 9" />
      {/* Rounded flask body */}
      <path d="M11 9 Q 4.5 13 5.5 18 Q 7 22 12 22 Q 17 22 18.5 18 Q 19.5 13 13 9" />
      {/* Floating bubbles inside the brew */}
      <circle cx="10" cy="17" r="0.65" fill="currentColor" stroke="none" />
      <circle cx="14" cy="15.2" r="0.45" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="0.55" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Updates. Scroll with an upward arrow. A rolled parchment being
// delivered, with an up-chevron indicating a new version / upgrade.
function IconScrollUpdate(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M6 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z" />
      <path d="M6 6H4a1 1 0 0 0 0 2h2" />
      <path d="M18 18h2a1 1 0 0 1 0 2h-2" />
      <path d="M12 16V9" />
      <path d="m9 12 3-3 3 3" />
    </svg>
  );
}

// Imposter Mask. Venetian "Volto" full-face mask, the canonical
// Carnevale silhouette: egg-shaped face, almond eye-slits, a slim
// nose ridge running to a small closed mouth, and a forehead jewel
// that reads as the gilded medallion the historical mask carried at
// the brow. The proportions are tuned so the silhouette stays
// recognizable at button sizes (1.4–2cqw). At smaller sizes the
// mouth line collapses cleanly into the lower mask plane and the
// jewel still pops as a distinct shape.
//
// `crossed` (boolean). When true, overlays a diagonal slash across
// the icon to signal "mask is OFF" (the inspector is showing the
// imposter underneath). The slash is drawn as a dark underlay plus
// a foreground stroke so it stays legible across the filled eyes,
// nose, and mask edges.
function IconMask({ crossed = false, ...props }) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      {/* Face outline. Inverted teardrop. Wider at the forehead and
          cheekbones, tapering to a softly-pointed chin. Drawn as a
          single closed Bézier so the silhouette reads as one mask
          rather than an outline of features stacked on a background. */}
      <path d="M12 2.5 C 16.5 2.5 19 6 19 11.5 C 19 16 17 20 14 21.3 C 12.7 21.8 11.3 21.8 10 21.3 C 7 20 5 16 5 11.5 C 5 6 7.5 2.5 12 2.5 Z" />
      {/* Forehead jewel. Small diamond between the brows. The Volto's
          gilded medallion. Filled so it reads as a separate ornament
          and not just a crease in the mask. */}
      <path d="M12 4.5 L 13.1 6 L 12 7.5 L 10.9 6 Z" fill="currentColor" stroke="none" />
      {/* Eye slits. Almond shapes with a subtle downward tilt for
          the stoic Volto expression. Stroked outline so the mask
          reads as a mask (eyes are HOLES, not painted features),
          but the strokes are heavy enough to look solid at small
          sizes. */}
      <path d="M7.5 11 C 8.2 9.7 10.2 9.7 10.9 11 C 10.2 12.3 8.2 12.3 7.5 11 Z" fill="currentColor" stroke="none" />
      <path d="M13.1 11 C 13.8 9.7 15.8 9.7 16.5 11 C 15.8 12.3 13.8 12.3 13.1 11 Z" fill="currentColor" stroke="none" />
      {/* Nose ridge. Single vertical line from the bridge to the
          philtrum. Thin so it doesn't compete with the eyes. */}
      <path d="M12 12.4 L 12 16" strokeWidth="1.2" />
      {/* Mouth. Small serene curve. The Volto's silent neutral
          expression. */}
      <path d="M10.2 17.6 Q 12 18.6 13.8 17.6" strokeWidth="1.2" />
      {crossed && (
        <>
          {/* Knockout underlay. Wider darker stroke that "punches
              through" the mask details so the foreground slash stays
              readable where it crosses the eyes and nose. */}
          <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" stroke="rgba(0,0,0,0.85)" strokeWidth="3.6" strokeLinecap="round" />
          <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" strokeWidth="2.2" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

// Warning triangle with center exclamation. Used to badge
// experimental / under-construction UI affordances so players know
// to expect rough edges before they engage with the flow.
function IconWarning(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M12 3 2 20h20Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" strokeLinecap="round" />
    </svg>
  );
}

// Outward diagonal arrows. "expand this square" affordance on crowded
// board cells (opens the cell fan).
function IconExpand(props) {
  return (
    <svg {...baseProps} {...withSize(props)}>
      <path d="M15 3h6v6" />
      <path d="M21 3l-7 7" />
      <path d="M9 21H3v-6" />
      <path d="M3 21l7-7" />
    </svg>
  );
}

export {
  CoinIcon,
  ShardIcon,
  IconEyeOpen,
  IconEyeClosed,
  IconExpand,
  KeystoneIcon,
  IconCheck,
  IconWarning,
  IconLink,
  IconChevronDown,
  IconChartBar,
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconDownload,
  IconMagic,
  IconMenu,
  IconPlus,
  IconPrint,
  IconSave,
  IconSearch,
  IconSettings,
  IconSparkles,
  IconTrash,
  IconScryingOrb,
  IconEye,
  IconKeyboard,
  IconBell,
  IconDiscord,
  IconCrown,
  IconMask,
  IconScrollUpdate,
  IconSkillTree,
  IconCoinSack,
  IconAnvil,
  IconSpellbook,
  IconTome,
  IconAtlas,
  IconLongbow,
  IconSwords,
  IconLaurels,
  IconSeasonScroll,
  IconAlchemyFlask,
  IconRuneFehu,
  IconRuneKenaz,
  IconRuneAnsuz,
  IconRuneAlgiz,
  IconRuneDagaz,
};
